import React, { PropsWithChildren, createContext, useCallback, useContext, useEffect, useState, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { User } from '../types/user';
import { useClient } from '../hooks/useClient';
import useSWR from 'swr';
import { Role } from '../types/role';
import { Feature } from '../config/features';
import storage from '../storage/storage';
import { notificationService } from '../services/NotificationService';
import { FORCED_LOGOUT_ROUTES } from '../config/pages';
import { OTA_UPDATE_PAGE_PATH } from '../utils/otaUtils';
import { APP_UPDATE_PAGE_PATH } from '../config/appUpdate';
import { isAuthError } from '../utils/authErrors';

export const ACCESS_TOKEN_STORAGE_KEY = 'accessToken';
export const USER_ID_STORAGE_KEY = 'userId';

/**
 * Pages that run while the backend refuses this app version (OTA bundle or store build too old). The user and the
 * role are not requested there: the backend would answer the same version error again.
 */
const UPDATE_PAGE_PATHS = [OTA_UPDATE_PAGE_PATH, APP_UPDATE_PAGE_PATH];

/**
 * The user and the role requests are retried after a network error or a 5xx (3 times, exponential backoff from 2 s)
 * and again when the network comes back. An auth error is not retried: it ends the session.
 */
const SESSION_REQUEST_OPTIONS = {
  shouldRetryOnError: true,
  errorRetryCount: 3,
  errorRetryInterval: 2000,
  revalidateOnReconnect: true,
};

interface LoginResponse {
  name: string;
  avatarUrl?: string;
  id: string;
  token: string;
  roles: string[];
}

interface AuthContextValue {
  signOut: () => Promise<void>;
  removeAuthTokens: () => Promise<void>;
  user: User | undefined;
  userLoading: boolean;
  login: (email: string, password: string, pushNotificationToken?: string | null) => Promise<void>;
  updateUser: (user: Partial<User>, avatarFile?: File) => Promise<User | undefined>;
  isExpectedLoggedIn: boolean;
  features: Feature[];
  featuresLoading: boolean;
  isAuthenticating: boolean;
  setUser: (newUser: User | undefined) => void;
  /** A session is stored, but the user or the role could not load (network error, 5xx). The session is kept. */
  sessionUnavailable?: boolean;
  /** Requests the user and the role again. */
  retrySession?: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [accessTokenLoading, setAccessTokenLoading] = useState<boolean>(true);
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(true);
  const initialPath = useRef(window.location.pathname);
  const isUpdatePage = UPDATE_PAGE_PATHS.includes(initialPath.current);
  const { post, patch } = useClient();
  // Session requests reject on failure, so fetchSessionResource can tell an auth error from a network error.
  // Inside AuthProvider, useClient has no signOut (the context is not provided yet): fetchSessionResource signs out.
  const sessionClient = useClient({ throwOnError: true });

  const signOut = useCallback(async () => {
    setIsAuthenticating(true);
    try {
      await storage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      await storage.removeItem(USER_ID_STORAGE_KEY);
      setSessionId(null);
      setAccessToken(null);
      window.location.assign('/');
    } catch {
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  const removeAuthTokens = useCallback(async () => {
    try {
      await storage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      await storage.removeItem(USER_ID_STORAGE_KEY);
      setSessionId(null);
      setAccessToken(null);
    } catch (error) {
      console.error('Error removing auth tokens:', error);
    }
  }, []);

  /**
   * Requests a session resource. Only an auth error signs the user out. Any other failure (network error, 5xx, an
   * empty answer) is thrown, so SWR keeps the stored session and the last data, and retries.
   */
  const fetchSessionResource = async <T,>(url: string): Promise<T | undefined> => {
    try {
      const data = await sessionClient.get<T>(url);
      if (!data) throw new Error(`Empty response from ${url}`);
      return data;
    } catch (error) {
      if (isAuthError(error)) {
        await signOut();
        return undefined;
      }
      throw error;
    }
  };

  const fetchUser = ({ sessionIdForUser }: { sessionIdForUser: string }) =>
    fetchSessionResource<User>(`/users/${sessionIdForUser}`);

  const {
    data: user,
    isLoading: userLoading,
    error: userError,
    mutate: mutateUser,
  } = useSWR(sessionId && !isUpdatePage ? { sessionIdForUser: sessionId } : null, fetchUser, SESSION_REQUEST_OPTIONS);

  const setUser = useCallback(
    (newUser: User | undefined) => {
      mutateUser(newUser, false);
    },
    [mutateUser],
  );

  useEffect(() => {
    if (!accessTokenLoading && !userLoading) {
      setIsAuthenticating(false);
    }
  }, [accessTokenLoading, userLoading]);

  const initializeAuth = async () => {
    try {
      const isForcedLogoutRoute = FORCED_LOGOUT_ROUTES.some((route) => {
        const regex = new RegExp(`^${route.replace(/:\w+/g, '[\\w.-]+')}$`);
        return regex.test(initialPath.current);
      });

      if (isForcedLogoutRoute) {
        await removeAuthTokens();
      }

      const [storedSession, storedToken] = await Promise.all([
        storage.getItem(USER_ID_STORAGE_KEY),
        storage.getItem(ACCESS_TOKEN_STORAGE_KEY),
      ]);

      setSessionId(storedSession);
      setAccessToken(storedToken);
    } catch (error) {
      // Do not delete the stored session: a storage read error does not mean the session is invalid.
      // Without a session in memory, the app shows the login page; the next start reads the storage again.
      console.error('Error initializing auth:', error);
    } finally {
      setAccessTokenLoading(false);
    }
  };

  useEffect(() => {
    initializeAuth();
  }, []);

  // Bumped when the user id changes and on unmount: a request still waiting on the device token is then not sent (#84).
  const pushTokenRun = useRef(0);
  const updatePushToken = useCallback(async () => {
    if (!user) return;
    const run = pushTokenRun.current;
    notificationService.init(user.id);
    let token = await notificationService.initialize();
    // The device can register after the 5 s wait of initialize(): ask once more on native (#358). Not on the web, where
    // a second ask can show the permission prompt again.
    if (!token && Capacitor.isNativePlatform() && run === pushTokenRun.current) token = await notificationService.initialize();
    if (!token || run !== pushTokenRun.current) return;
    console.log('Updating user push token: ', token);
    await post('/users/update-push-token', {
      body: {
        token,
      },
    });
  }, [post, user]);

  // `post` is a new function on every render (useClient), so the token is sent once per user id, with the latest
  // updatePushToken, not on every render (#84).
  const updatePushTokenRef = useRef(updatePushToken);
  useEffect(() => {
    updatePushTokenRef.current = updatePushToken;
  }, [updatePushToken]);
  const userId = user?.id;
  useEffect(() => {
    updatePushTokenRef.current();
    return () => {
      pushTokenRun.current += 1;
    };
  }, [userId]);

  const fetchFeatures = async (url: string): Promise<Feature[] | undefined> => {
    const userRole = await fetchSessionResource<Role>(url);
    return userRole?.features;
  };

  const {
    data: featuresData,
    isLoading: featuresLoading,
    error: featuresError,
    mutate: mutateFeatures,
  } = useSWR(sessionId && !isUpdatePage ? `/users/${sessionId}/role` : null, fetchFeatures, SESSION_REQUEST_OPTIONS);
  const features = featuresData ?? [];

  // SWR keeps the last data when a later request fails, so a refresh while offline keeps a loaded session working.
  // SWR keeps the error until a request succeeds, so this stays true while a retry is loading: the app never mounts
  // its pages without a user in between.
  const sessionUnavailable =
    !!sessionId && !isUpdatePage && ((!user && !!userError) || (featuresData === undefined && !!featuresError));

  const retrySession = useCallback(async () => {
    await Promise.all([mutateUser(), mutateFeatures()]);
  }, [mutateUser, mutateFeatures]);

  const login = async (email: string, password: string) => {
    const loginResponse = await post<LoginResponse>('/login', {
      body: {
        email,
        password,
      },
      errorMessages: {
        summary: 'Error en el inicio de sesión',
        defaultDetail: 'No se ha podido iniciar la sesión',
        401: 'Los datos introducidos no son válidos',
      },
    });

    const { id, token } = loginResponse ?? {};
    if (id && token) {
      await storage.setItem(USER_ID_STORAGE_KEY, id);
      await storage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
      setSessionId(id);
    }
  };

  const updateUser = async (userData: Partial<User>, avatarFile?: File) => {
    const newUser: Partial<User> = { ...userData };
    delete newUser.id;
    const formData = new FormData();

    const { dateOfBirth, phone, ...rest } = newUser;

    formData.append(
      'data',
      JSON.stringify({
        ...rest,
        ...(dateOfBirth ? { dateOfBirth } : { dateOfBirth: null }),
        ...(phone ? { phone } : { phone: null }),
      }),
    );
    if (avatarFile) {
      formData.append('image', avatarFile);
    }

    const updatedUser = await patch<User>(`/users/${userData?.id}`, {
      body: formData,
      errorMessages: {
        summary: 'Error al editar perfil',
        defaultDetail: 'No se ha podido editar el perfil de usuario',
      },
      successMessage: {
        summary: 'Perfil editado',
        detail: 'Se ha editado correctamente el perfil de usuario',
      },
    });

    if (updatedUser) {
      await storage.setItem(USER_ID_STORAGE_KEY, updatedUser.id);
      setUser(updatedUser);
      setSessionId(updatedUser.id);
      return updatedUser;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        signOut,
        user,
        userLoading,
        login,
        updateUser,
        isExpectedLoggedIn: !!accessToken && !accessTokenLoading,
        features,
        featuresLoading,
        isAuthenticating,
        removeAuthTokens,
        setUser,
        sessionUnavailable,
        retrySession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// App renders every page inside AuthProvider (App.tsx), so the value is always there.
export const useAuthContext = () => useContext(AuthContext) as AuthContextValue;

// AuthProvider itself calls useClient before its own value exists: that one caller gets null here.
export const useOptionalAuthContext = () => useContext(AuthContext);
